/**
 * Owns Box application setup and user OAuth inside the connector dialog.
 *
 * Box needs one workspace-scoped application credential plus one user-scoped
 * OAuth connection. Keeping both here makes the connector the only setup
 * surface and fails closed where AppHosting has not provisioned encrypted,
 * application-writable credential storage.
 */
import { useState } from 'react'
import { RotateCw, Save, ShieldAlert, Trash2, Unplug } from 'lucide-react'
import { toast } from 'sonner'

import type { NodeApiClient } from '@/app-shell/api/node-api-client'
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Input,
  Label,
} from '@/components/ui'
import {
  deleteBoxApplicationCredentials,
  saveBoxApplicationCredentials,
} from '../services/api'
import type { BoxOAuthConfig } from '../types'

export function BoxConnection({
  api,
  orgId,
  vaultId,
  token,
  config,
  loading,
  onConfigChanged,
  onConnect,
  onDisconnect,
}: {
  api: NodeApiClient
  orgId: string
  vaultId: string
  token: string | null
  config?: BoxOAuthConfig
  loading: boolean
  onConfigChanged: () => Promise<void>
  onConnect: () => void
  onDisconnect: () => Promise<boolean>
}) {
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  const [saving, setSaving] = useState(false)

  if (loading) {
    return <p className="text-xs text-slate-500">Loading Box configuration…</p>
  }

  const storageAvailable = config?.oauthStorageConfigured === true
  const configured = config?.configured === true
  const save = async () => {
    setSaving(true)
    try {
      await saveBoxApplicationCredentials(
        api,
        orgId,
        vaultId,
        clientId,
        clientSecret,
      )
      setClientId('')
      setClientSecret('')
      await onConfigChanged()
      toast.success('Box application credential stored')
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? 'Box credential could not be stored')
    } finally {
      setSaving(false)
    }
  }
  const remove = async () => {
    setSaving(true)
    try {
      await deleteBoxApplicationCredentials(api, orgId, vaultId)
      await onConfigChanged()
      toast.success('Box application credential deleted')
    } catch (error: any) {
      toast.error(error?.response?.data?.detail ?? 'Box credential could not be deleted')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      {!storageAvailable && (
        <Alert>
          <AlertTitle>Box is not currently in use</AlertTitle>
          <AlertDescription>
            Box will be available after AppHosting provides the new writable
            credential store for application credentials and user OAuth tokens.
          </AlertDescription>
        </Alert>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="box-oauth-client-id">Box OAuth client ID</Label>
          <Input
            id="box-oauth-client-id"
            type="password"
            autoComplete="off"
            value={clientId}
            disabled={!storageAvailable || saving}
            onChange={event => setClientId(event.target.value)}
            placeholder={configured ? '•••••••• — replace' : 'Application client ID'}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="box-oauth-client-secret">Box OAuth client secret</Label>
          <Input
            id="box-oauth-client-secret"
            type="password"
            autoComplete="off"
            value={clientSecret}
            disabled={!storageAvailable || saving}
            onChange={event => setClientSecret(event.target.value)}
            placeholder={configured ? '•••••••• — replace' : 'Application client secret'}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-[11px] leading-4 text-amber-800">
          <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
          Use the OAuth application issued for this integration, never personal credentials.
        </p>
        <div className="flex gap-2">
          {configured && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive"
              disabled={saving}
              onClick={() => void remove()}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!storageAvailable || saving || !clientId || !clientSecret}
            onClick={() => void save()}
          >
            <Save className="mr-1.5 h-3.5 w-3.5" /> Store credential
          </Button>
        </div>
      </div>

      {configured && storageAvailable && (
        token ? (
          <div className="flex items-center gap-3 rounded-[9px] border border-slate-200 px-3 py-2.5">
            <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-indigo-50 text-[11px] font-semibold text-indigo-700">
              BX
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] font-semibold text-slate-900">
                Box account connected
              </span>
              <span className="block truncate text-xs text-slate-500">
                Encrypted connection · renewed automatically when needed
              </span>
            </span>
            <span className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={onConnect}>
                <RotateCw className="mr-1.5 h-3.5 w-3.5" /> Reconnect
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive"
                onClick={() => void onDisconnect()}
              >
                <Unplug className="mr-1.5 h-3.5 w-3.5" /> Disconnect account
              </Button>
            </span>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button type="button" size="sm" onClick={onConnect}>
              Connect Box account
            </Button>
            {config.connected && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-destructive"
                onClick={() => void onDisconnect()}
              >
                <Unplug className="mr-1.5 h-3.5 w-3.5" /> Disconnect account
              </Button>
            )}
          </div>
        )
      )}
    </div>
  )
}
